/**
 * VSC daily reports API endpoints.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from './api-client';
import type { PaginatedResponse } from '@/types/data-table.types';
import type {
  CreateVscReportDto,
  CreateVscReportResult,
  UpdateVscReportDto,
  VscReport,
  GetAllVscReportsParams,
  ExportVscReportsParams,
} from '@/types/vsc-report.types';

/** VSC daily-report API prefix (`GET|POST /sessions/vsc-daily-reports`). */
const VSC_REPORTS_API = '/sessions/vsc-daily-reports';

/** `GET /sessions/vsc-daily-reports` — paginated VSC reports. */
export const getAllVscReports = async (
  params?: GetAllVscReportsParams,
): Promise<PaginatedResponse<VscReport>> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  if (params?.establishmentId) searchParams.append('establishmentId', params.establishmentId);
  if (params?.reportDate) searchParams.append('reportDate', params.reportDate);
  if (params?.reportDateFrom) searchParams.append('reportDateFrom', params.reportDateFrom);
  if (params?.reportDateTo) searchParams.append('reportDateTo', params.reportDateTo);
  if (params?.status) searchParams.append('status', params.status);
  params?.statuses?.forEach((s) => searchParams.append('statuses', s));
  if (params?.search) searchParams.append('search', params.search);
  if (params?.includeNotYetVisible) searchParams.append('includeNotYetVisible', 'true');
  const qs = searchParams.toString();
  const url = qs ? `${VSC_REPORTS_API}?${qs}` : VSC_REPORTS_API;
  const response = await apiClient.get<PaginatedResponse<VscReport>>(url);
  return response.data;
};

/** `GET /sessions/vsc-daily-reports?exportExcel=true` — download XLSX. */
export const exportVscReportsExcel = async (params?: ExportVscReportsParams): Promise<void> => {
  const searchParams = new URLSearchParams();
  searchParams.append('exportExcel', 'true');
  if (params?.establishmentId) searchParams.append('establishmentId', params.establishmentId);
  if (params?.reportDate) searchParams.append('reportDate', params.reportDate);
  if (params?.reportDateFrom) searchParams.append('reportDateFrom', params.reportDateFrom);
  if (params?.reportDateTo) searchParams.append('reportDateTo', params.reportDateTo);
  if (params?.status) searchParams.append('status', params.status);
  params?.statuses?.forEach((s) => searchParams.append('statuses', s));
  if (params?.search) searchParams.append('search', params.search);
  if (params?.includeNotYetVisible) searchParams.append('includeNotYetVisible', 'true');

  const response = await apiClient.get(`${VSC_REPORTS_API}?${searchParams.toString()}`, {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `vsc-reports-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

/** `POST /sessions/vsc-daily-reports` */
export const createVscReports = async (dto: CreateVscReportDto): Promise<CreateVscReportResult> => {
  const response = await apiClient.post<CreateVscReportResult>(VSC_REPORTS_API, dto);
  return response.data;
};

/** `GET /sessions/vsc-daily-reports/:reportId` */
export const getVscReport = async (reportId: string): Promise<VscReport> => {
  const response = await apiClient.get<VscReport>(`${VSC_REPORTS_API}/${reportId}`);
  return response.data;
};

/** `PATCH /sessions/vsc-daily-reports/:reportId` */
export const updateVscReport = async (
  reportId: string,
  dto: UpdateVscReportDto,
): Promise<VscReport> => {
  const response = await apiClient.patch<VscReport>(`${VSC_REPORTS_API}/${reportId}`, dto);
  return response.data;
};

/** `PATCH /sessions/vsc-daily-reports/:reportId/validate` */
export const validateVscReport = async (reportId: string): Promise<VscReport> => {
  const response = await apiClient.patch<VscReport>(`${VSC_REPORTS_API}/${reportId}/validate`);
  return response.data;
};

/** `DELETE /sessions/vsc-daily-reports/:reportId` */
export const deleteVscReport = async (reportId: string): Promise<void> => {
  await apiClient.delete(`${VSC_REPORTS_API}/${reportId}`);
};
